/**
 * PolicyEditor
 *
 * Admin component for editing a group's capability policy.
 * Manages per-resource allow/deny/neutral toggles, wildcard toggles,
 * quota fields, and a deny-warning banner.
 *
 * Props:
 *   groupId {string}     - ID of the group to edit
 *   orgId   {string}     - Organisation ID
 *   onSaved {function?}  - Called after a successful save
 */

import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from "react";
import { AlertTriangle, CheckCircle, ChevronDown, MinusCircle, XCircle } from "lucide-react";
import api from "../../api/index";
import ENDPOINTS from "../../api/endpoints";
import { getGroupPolicy, setGroupPolicy } from "../../api/adminGroups";
import { getModelList } from "../../api/getModels";
import Loader from "../Loader/Loader";
import logger from "../../services/Logger";
import { isDirty } from "../../utils/isDirty";

// ─── Constants ────────────────────────────────────────────────────────────────

const EMPTY_POLICY = {
  allowed_models: [],
  denied_models: [],
  allowed_agents: [],
  denied_agents: [],
  allowed_skills: [],
  denied_skills: [],
  allowed_docs_capabilities: [],
  denied_docs_capabilities: [],
  quota_daily_tokens: "",
  quota_monthly_tokens: "",
  quota_requests_per_minute: "",
  quota_daily_image_generations: "",
  quota_daily_video_generations: "",
  quota_daily_audio_generations: "",
};

const DOCS_CAPABILITIES = [
  { id: "docs.product.read", label: "Product and developer guides" },
  { id: "docs.settings.users.read", label: "User and group settings" },
  { id: "docs.settings.identity.read", label: "Identity settings" },
  { id: "docs.forge.read", label: "Forge" },
  { id: "docs.architecture.read", label: "Architecture" },
  { id: "docs.deployment.read", label: "Deployment" },
  { id: "docs.security.read", label: "Security" },
  { id: "docs.operations.read", label: "Operations" },
  { id: "docs.internal.read", label: "Photon internal" },
];

const BUILTIN_POLICY_SKILLS = [
  { skill_id: "web-search-skill", display_name: "Web Search & URL Fetch (Built-in)" },
];

// ─── Helpers ──────────────────────────────────────────────────────────────────

/** Returns "allow" | "deny" | "neutral" for a given resource id. */
function getStance(id, allowedList, deniedList) {
  if (allowedList.includes(id)) return "allow";
  if (deniedList.includes(id)) return "deny";
  return "neutral";
}

/**
 * Toggle a resource's stance in the policy.
 * Cycles: neutral → allow → deny → neutral
 * Wildcard ("*") follows the same cycle.
 */
function cycleStance(current) {
  if (current === "neutral") return "allow";
  if (current === "allow") return "deny";
  return "neutral";
}

function applyStance(policy, resourceType, resourceId, newStance) {
  const allowKey = `allowed_${resourceType}`;
  const denyKey = `denied_${resourceType}`;
  const newAllowed = policy[allowKey].filter((x) => x !== resourceId);
  const newDenied = policy[denyKey].filter((x) => x !== resourceId);
  if (newStance === "allow") newAllowed.push(resourceId);
  if (newStance === "deny") newDenied.push(resourceId);
  return { ...policy, [allowKey]: newAllowed, [denyKey]: newDenied };
}

// ─── Sub-components ───────────────────────────────────────────────────────────

const StanceButton = ({ stance, onToggle }) => {
  const configs = {
    allow: {
      icon: <CheckCircle className="w-4 h-4" />,
      label: "Allow",
      className: "bg-fill-green-100 dark:bg-fill-green-900/30 text-ink-green-700 dark:text-ink-green-400 border-edge-green-300 dark:border-edge-green-700",
    },
    deny: {
      icon: <XCircle className="w-4 h-4" />,
      label: "Deny",
      className: "bg-fill-red-100 dark:bg-fill-red-900/30 text-ink-red-700 dark:text-ink-red-400 border-edge-red-300 dark:border-edge-red-700",
    },
    neutral: {
      icon: <MinusCircle className="w-4 h-4" />,
      label: "Inherit",
      className: "bg-fill-gray-100 dark:bg-fill-gray-700 text-ink-gray-500 dark:text-ink-gray-400 border-edge-gray-300 dark:border-edge-gray-600",
    },
  };
  const cfg = configs[stance];
  return (
    <button
      type="button"
      onClick={onToggle}
      className={`flex items-center gap-1 text-xs font-medium px-2 py-1 rounded border transition-colors min-w-[80px] ${cfg.className}`}
    >
      {cfg.icon}
      {cfg.label}
    </button>
  );
};

const ResourceRow = ({ label, id, allowedList, deniedList, resourceType, onPolicyChange, policy }) => {
  const stance = getStance(id, allowedList, deniedList);
  return (
    <div className="flex items-center justify-between py-2 border-b border-edge-gray-100 dark:border-edge-gray-700 last:border-0">
      <span className="text-sm text-ink-ink dark:text-ink-white truncate flex-1 mr-4">{label}</span>
      <StanceButton
        stance={stance}
        onToggle={() => onPolicyChange(applyStance(policy, resourceType, id, cycleStance(stance)))}
      />
    </div>
  );
};

/**
 * Counts individual (non-wildcard) overrides in the allow/deny lists.
 * Used to decide default open state and to render the summary pill.
 */
function countOverrides(allowedList, deniedList) {
  const allowed = allowedList.filter((x) => x !== "*").length;
  const denied = deniedList.filter((x) => x !== "*").length;
  return { allowed, denied };
}

const CollapsibleSection = ({ title, allowedList, deniedList, resourceType, onPolicyChange, policy, children }) => {
  const { allowed, denied } = countOverrides(allowedList, deniedList);
  const hasOverrides = allowed > 0 || denied > 0;
  const [open, setOpen] = useState(hasOverrides);
  const wildcardStance = getStance("*", allowedList, deniedList);

  return (
    <div className="rounded-lg border border-edge-border dark:border-edge-gray-700">
      {/* Clickable header row */}
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between px-4 py-3 text-left"
      >
        <div className="flex items-center gap-2">
          <ChevronDown
            className={`w-4 h-4 text-ink-gray-400 transition-transform duration-200 ${
              open ? "rotate-0" : "-rotate-90"
            }`}
          />
          <h4 className="text-sm font-semibold text-ink-ink dark:text-ink-white">{title}</h4>
          {/* Summary pill — only shown when collapsed and there are overrides */}
          {!open && hasOverrides && (
            <span className="flex items-center gap-1.5 text-xs text-ink-gray-500 dark:text-ink-gray-400">
              {allowed > 0 && (
                <span className="px-1.5 py-0.5 rounded bg-fill-green-100 dark:bg-fill-green-900/30 text-ink-green-700 dark:text-ink-green-400">
                  {allowed} allowed
                </span>
              )}
              {denied > 0 && (
                <span className="px-1.5 py-0.5 rounded bg-fill-red-100 dark:bg-fill-red-900/30 text-ink-red-700 dark:text-ink-red-400">
                  {denied} denied
                </span>
              )}
            </span>
          )}
        </div>
        {/* Wildcard toggle — stop propagation so clicking it doesn't fold/unfold */}
        <div
          className="flex items-center gap-2"
          onClick={(e) => e.stopPropagation()}
        >
          <span className="text-xs text-ink-gray-500 dark:text-ink-gray-400">All {(title || "").toLowerCase()}</span>
          <StanceButton
            stance={wildcardStance}
            onToggle={() => onPolicyChange(applyStance(policy, resourceType, "*", cycleStance(wildcardStance)))}
          />
        </div>
      </button>

      {/* Collapsible body */}
      {open && (
        <div className="px-4 pb-4 border-t border-edge-border dark:border-edge-gray-700">
          {children}
        </div>
      )}
    </div>
  );
};

// ─── PolicyEditor ─────────────────────────────────────────────────────────────

const PolicyEditor = forwardRef(({ groupId, orgId, onSaved, onSavingChange, onDirtyChange }, ref) => {
  const [policy, setPolicy] = useState(EMPTY_POLICY);
  // Snapshot of the loaded policy, used to report dirty state up to the
  // parent's external Save Policy button via onDirtyChange (see below) —
  // the button lives in GroupDetailPanel.js, not in this component.
  const [originalPolicy, setOriginalPolicy] = useState(EMPTY_POLICY);
  const [models, setModels] = useState([]);
  const [agents, setAgents] = useState([]);
  const [skills, setSkills] = useState([]);
  const [loading, setLoading] = useState(true);
  const [, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  const loadResources = useCallback(async () => {
    setLoading(true);
    try {
      const [policyData, modelData, agentData, skillData] = await Promise.allSettled([
        getGroupPolicy(groupId, orgId),
        getModelList(),
        api({ method: "POST", url: ENDPOINTS.GET_AGENTS, data: { org_id: orgId } }),
        api({ method: "POST", url: "/agent/skills/list", data: { org_id: orgId } }),
      ]);

      if (!mountedRef.current) return;

      if (policyData.status === "fulfilled" && policyData.value) {
        const p = policyData.value;
        const loaded = {
          allowed_models: p.allowed_models ?? [],
          denied_models: p.denied_models ?? [],
          allowed_agents: p.allowed_agents ?? [],
          denied_agents: p.denied_agents ?? [],
          allowed_skills: p.allowed_skills ?? [],
          denied_skills: p.denied_skills ?? [],
          allowed_docs_capabilities: p.allowed_docs_capabilities ?? [],
          denied_docs_capabilities: p.denied_docs_capabilities ?? [],
          quota_daily_tokens: p.quota_daily_tokens ?? "",
          quota_monthly_tokens: p.quota_monthly_tokens ?? "",
          quota_requests_per_minute: p.quota_requests_per_minute ?? "",
          quota_daily_image_generations: p.quota_daily_image_generations ?? "",
          quota_daily_video_generations: p.quota_daily_video_generations ?? "",
          quota_daily_audio_generations: p.quota_daily_audio_generations ?? "",
        };
        setPolicy(loaded);
        setOriginalPolicy(loaded);
      }

      if (modelData.status === "fulfilled") {
        setModels(modelData.value?.models ?? []);
      }
      if (agentData.status === "fulfilled") {
        setAgents(agentData.value?.data?.agents ?? []);
      }
      if (skillData.status === "fulfilled") {
        setSkills(skillData.value?.data?.skills ?? []);
      }
    } catch (err) {
      logger.error("PolicyEditor", "Error loading resources", { error: err.message });
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }, [groupId, orgId]);

  useEffect(() => {
    loadResources();
  }, [loadResources]);

  const handleSave = async () => {
    setSaving(true);
    onSavingChange?.(true);
    setSaveError(null);
    try {
      const policyPayload = {
        ...policy,
        quota_daily_tokens: policy.quota_daily_tokens === "" ? null : Number(policy.quota_daily_tokens),
        quota_monthly_tokens: policy.quota_monthly_tokens === "" ? null : Number(policy.quota_monthly_tokens),
        quota_requests_per_minute: policy.quota_requests_per_minute === "" ? null : Number(policy.quota_requests_per_minute),
        quota_daily_image_generations: policy.quota_daily_image_generations === "" ? null : Number(policy.quota_daily_image_generations),
        quota_daily_video_generations: policy.quota_daily_video_generations === "" ? null : Number(policy.quota_daily_video_generations),
        quota_daily_audio_generations: policy.quota_daily_audio_generations === "" ? null : Number(policy.quota_daily_audio_generations),
      };
      await setGroupPolicy(groupId, policyPayload);
      setOriginalPolicy(policy);
      if (onSaved) onSaved();
    } catch (err) {
      setSaveError("Failed to save policy. Please try again.");
      logger.error("PolicyEditor", "Error saving policy", { error: err.message });
    } finally {
      setSaving(false);
      onSavingChange?.(false);
    }
  };

  useImperativeHandle(ref, () => ({ save: handleSave }));

  // Report dirty state up to the parent's external Save Policy button
  // whenever the policy or its baseline changes.
  useEffect(() => {
    onDirtyChange?.(isDirty(policy, originalPolicy));
  }, [policy, originalPolicy, onDirtyChange]);

  const hasDenies =
    policy.denied_models.length > 0 ||
    policy.denied_agents.length > 0 ||
    policy.denied_skills.length > 0 ||
    policy.denied_docs_capabilities.length > 0;

  const hasAnyOverrides =
    policy.allowed_models.length > 0 ||
    policy.denied_models.length > 0 ||
    policy.allowed_agents.length > 0 ||
    policy.denied_agents.length > 0 ||
    policy.allowed_skills.length > 0 ||
    policy.denied_skills.length > 0 ||
    policy.allowed_docs_capabilities.length > 0 ||
    policy.denied_docs_capabilities.length > 0 ||
    policy.quota_daily_tokens !== "" ||
    policy.quota_monthly_tokens !== "" ||
    policy.quota_requests_per_minute !== "" ||
    policy.quota_daily_image_generations !== "" ||
    policy.quota_daily_video_generations !== "" ||
    policy.quota_daily_audio_generations !== "";

  const handleResetAll = () => {
    setPolicy((prev) => ({
      ...prev,
      allowed_models: [],
      denied_models: [],
      allowed_agents: [],
      denied_agents: [],
      allowed_skills: [],
      denied_skills: [],
      allowed_docs_capabilities: [],
      denied_docs_capabilities: [],
      quota_daily_tokens: "",
      quota_monthly_tokens: "",
      quota_requests_per_minute: "",
      quota_daily_image_generations: "",
      quota_daily_video_generations: "",
      quota_daily_audio_generations: "",
    }));
  };

  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <Loader />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-5">
      {/* Deny warning banner */}
      {hasDenies && (
        <div className="flex items-start gap-2 bg-fill-hex-fffbeb dark:bg-fill-hex-78350f/20 border border-edge-amber-200 dark:border-edge-amber-700 rounded-lg px-3 py-2">
          <AlertTriangle className="w-4 h-4 text-ink-hex-d97706 dark:text-ink-amber-400 mt-0.5 flex-shrink-0" />
          <p className="text-xs text-ink-amber-700 dark:text-ink-amber-400">
            Explicit denies override all group allows. A user in multiple groups will be denied if any group denies them access.
          </p>
        </div>
      )}

      {/* Reset all overrides */}
      {hasAnyOverrides && (
        <div className="flex justify-end">
          <button
            type="button"
            onClick={handleResetAll}
            className="text-xs text-ink-gray-500 dark:text-ink-gray-400 hover:text-ink-red-600 dark:hover:text-ink-red-400 underline underline-offset-2 transition-colors"
          >
            Reset all to Inherit
          </button>
        </div>
      )}

      {/* Models */}
      <CollapsibleSection
        title="Models"
        allowedList={policy.allowed_models}
        deniedList={policy.denied_models}
        resourceType="models"
        onPolicyChange={setPolicy}
        policy={policy}
      >
        {models.length === 0 ? (
          <p className="text-xs text-ink-gray-500 dark:text-ink-gray-400 pt-3">No models configured for this org.</p>
        ) : (
          models.map((m) => (
            <ResourceRow
              key={m.model_id}
              id={m.model_id}
              label={m.display_name || m.model_id}
              allowedList={policy.allowed_models}
              deniedList={policy.denied_models}
              resourceType="models"
              onPolicyChange={setPolicy}
              policy={policy}
            />
          ))
        )}
      </CollapsibleSection>

      {/* Agents */}
      <CollapsibleSection
        title="Agents"
        allowedList={policy.allowed_agents}
        deniedList={policy.denied_agents}
        resourceType="agents"
        onPolicyChange={setPolicy}
        policy={policy}
      >
        {agents.length === 0 ? (
          <p className="text-xs text-ink-gray-500 dark:text-ink-gray-400 pt-3">No agents configured for this org.</p>
        ) : (
          agents.map((a) => (
            <ResourceRow
              key={a.agent_id}
              id={a.agent_id}
              label={a.name || a.agent_id}
              allowedList={policy.allowed_agents}
              deniedList={policy.denied_agents}
              resourceType="agents"
              onPolicyChange={setPolicy}
              policy={policy}
            />
          ))
        )}
      </CollapsibleSection>

      {/* Skills */}
      <CollapsibleSection
        title="Skills"
        allowedList={policy.allowed_skills}
        deniedList={policy.denied_skills}
        resourceType="skills"
        onPolicyChange={setPolicy}
        policy={policy}
      >
        {[
          ...BUILTIN_POLICY_SKILLS,
          ...skills.filter((s) => !BUILTIN_POLICY_SKILLS.some((b) => b.skill_id === s.skill_id)),
        ].map((s) => (
          <ResourceRow
            key={s.skill_id}
            id={s.skill_id}
            label={s.display_name || s.skill_name || s.skill_id}
            allowedList={policy.allowed_skills}
            deniedList={policy.denied_skills}
            resourceType="skills"
            onPolicyChange={setPolicy}
            policy={policy}
          />
        ))}
      </CollapsibleSection>

      {/* Documentation */}
      <CollapsibleSection
        title="Documentation"
        allowedList={policy.allowed_docs_capabilities}
        deniedList={policy.denied_docs_capabilities}
        resourceType="docs_capabilities"
        onPolicyChange={setPolicy}
        policy={policy}
      >
        {DOCS_CAPABILITIES.map((capability) => (
          <ResourceRow
            key={capability.id}
            id={capability.id}
            label={capability.label}
            allowedList={policy.allowed_docs_capabilities}
            deniedList={policy.denied_docs_capabilities}
            resourceType="docs_capabilities"
            onPolicyChange={setPolicy}
            policy={policy}
          />
        ))}
      </CollapsibleSection>

      {/* Quotas */}
      <div className="rounded-lg border border-edge-border dark:border-edge-gray-700 p-4">
        <h4 className="text-sm font-semibold text-ink-ink dark:text-ink-white mb-3">Quotas</h4>
        <p className="text-xs text-ink-gray-500 dark:text-ink-gray-400 mb-3">Leave blank to inherit from org default or apply no limit.</p>
        <div className="grid grid-cols-2 gap-3">
          {[
            { key: "quota_daily_tokens", label: "Daily tokens" },
            { key: "quota_monthly_tokens", label: "Monthly tokens" },
            { key: "quota_requests_per_minute", label: "Requests / min" },
            { key: "quota_daily_image_generations", label: "Daily images" },
            { key: "quota_daily_video_generations", label: "Daily videos" },
            { key: "quota_daily_audio_generations", label: "Daily audio" },
          ].map(({ key, label }) => (
            <div key={key}>
              <label className="text-xs text-ink-gray-500 dark:text-ink-gray-400 mb-1 block">{label}</label>
              <input
                type="number"
                min="0"
                value={policy[key]}
                onChange={(e) => setPolicy((prev) => ({ ...prev, [key]: e.target.value }))}
                placeholder="No limit"
                className="photon-field bg-fill-hex-fafbfe dark:bg-fill-navigation-surface-dark block w-full p-[8px] text-[13px] text-ink-ink dark:text-ink-white placeholder-ink-copy-hover outline-none border-[0.5px] border-edge-hex-aaaaaa dark:border-edge-hex-aaaaaa80 rounded-[10px] placeholder:italic"
              />
            </div>
          ))}
        </div>
      </div>

      {saveError && (
        <p className="text-xs text-ink-red-600 dark:text-ink-red-400">{saveError}</p>
      )}
    </div>
  );
});

PolicyEditor.displayName = "PolicyEditor";

export default PolicyEditor;
